import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Link component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 350:3256):
 * - Types: Primary (grey-950), Secondary (grey-500), Disabled (grey-300)
 * - Sizes: sm (12px), md (14px)
 * - Weight: Regular, Medium
 * - Always underlined
 */
const linkVariants = cva(
  'inline-flex items-center underline decoration-solid transition-colors',
  {
    variants: {
      variant: {
        primary: 'text-grey-950 hover:text-grey-700 dark:text-grey-50 dark:hover:text-grey-300',
        secondary: 'text-grey-500 hover:text-grey-700 dark:text-grey-400 dark:hover:text-grey-300',
        disabled: 'text-black/30 cursor-not-allowed dark:text-white/30',
      },
      size: {
        sm: 'text-xs leading-4 gap-0.5',
        md: 'text-sm leading-5 gap-1',
      },
      weight: {
        regular: 'font-normal',
        medium: 'font-medium',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      weight: 'medium',
    },
  }
);

const Link = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      weight,
      asChild = false,
      leftIcon,
      rightIcon,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'a';
    const effectiveVariant = disabled ? 'disabled' : variant;

    return (
      <Comp
        className={cn(linkVariants({ variant: effectiveVariant, size, weight, className }))}
        ref={ref}
        aria-disabled={disabled}
        {...props}
      >
        {leftIcon && <span className="shrink-0">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="shrink-0">{rightIcon}</span>}
      </Comp>
    );
  }
);
Link.displayName = 'Link';

export { Link, linkVariants };
